<template>
  <article :class="$style.card" ref="card">
    <div :class="$style.cardTop">
      <div :class="$style.authorName">{{ review.displayName }}</div>
      <span :class="$style.fbBadge">
        <IconFacebookCircle :class="$style.fbIcon" />
        {{ recommendedLabel }}
      </span>
    </div>

    <!--
      Accordion wraps the whole review body (preview + rest) so height animates
      as one block. Text stays inline via spans; the Accordion root is only a
      height clip, not a break between the two parts.
    -->
    <Accordion
      v-if="needsMore"
      ref="textAccordion"
      :class="$style.textAccordion"
      :isOpen="textAccordionOpen"
      :collapsedHeight="collapsedHeight"
      :isHeightAuto="false"
      :isInstant="!animateReady"
      :transitionTime="400"
    >
      <p :class="$style.reviewText">
        <span>{{ shortText }}</span
        ><template v-if="!isExpanded"
          >…&nbsp;<button type="button" :class="$style.readMore" @click="expand">
            {{ readMoreLabel }}
          </button></template
        ><span v-else>{{ restText }}</span>
      </p>
    </Accordion>
    <p v-else :class="$style.reviewText">{{ fullText }}</p>

    <div :class="$style.cardFooter">
      <span v-if="translatedFrom" :class="$style.translated">
        {{ translatedFromLabel }}
      </span>
      <span v-else></span>
      <a
        :class="$style.fbLink"
        :href="review.facebookUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ viewOnFacebookLabel }}
        <span aria-hidden="true">↗</span>
      </a>
    </div>
  </article>
</template>

<script>
import Accordion from '@/components/Accordion.vue'
import IconFacebookCircle from '@/components/_icons/IconFacebookCircle.svg'

const MAX_CHARS = 160
/** Only offer “Read more” when expanding reveals at least this many characters. */
const MIN_EXPAND_CHARS = 40

export default {
  name: 'HomeTestimonialsCard',

  components: {
    Accordion,
    IconFacebookCircle,
  },

  props: {
    review: {
      type: Object,
      required: true,
    },

    fullText: {
      type: String,
      required: true,
    },

    translatedFrom: {
      type: String,
      default: '',
    },

    recommendedLabel: {
      type: String,
      required: true,
    },

    readMoreLabel: {
      type: String,
      required: true,
    },

    viewOnFacebookLabel: {
      type: String,
      required: true,
    },

    translatedFromLabel: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isExpanded: false,
      collapsedHeight: 0,
      hasMeasured: false,
      animateReady: false,
    }
  },

  computed: {
    shortTextCandidate() {
      if (this.fullText.length <= MAX_CHARS) return this.fullText
      return this.fullText.slice(0, MAX_CHARS).replace(/\s+\S*$/, '')
    },

    needsMore() {
      const hiddenLength = this.fullText.length - this.shortTextCandidate.length
      return hiddenLength >= MIN_EXPAND_CHARS
    },

    shortText() {
      if (!this.needsMore) return this.fullText
      return this.shortTextCandidate
    },

    restText() {
      if (!this.needsMore) return ''
      return this.fullText.slice(this.shortTextCandidate.length)
    },

    // Natural height until measured, then clip to that same height (no 0px flash).
    textAccordionOpen() {
      return !this.hasMeasured || this.isExpanded
    },
  },

  mounted() {
    this.measureCollapsedHeight()
  },

  watch: {
    fullText() {
      // FR/EN wraps differ; remasure collapsed clip (or reflow open height if expanded).
      this.remeasureForTextChange()
    },
  },

  methods: {
    waitForLayout() {
      return new Promise(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(resolve))
      })
    },

    async remeasureForTextChange() {
      if (this.isExpanded) {
        await this.$nextTick()
        await this.waitForLayout()
        const accordion = this.$refs.textAccordion
        const el = accordion?.$el
        if (el) accordion.animateToHeight?.(el.scrollHeight)
        return
      }

      this.hasMeasured = false
      this.animateReady = false
      this.collapsedHeight = 0
      await this.measureCollapsedHeight()
    },

    async measureCollapsedHeight() {
      if (!this.needsMore) return

      await this.$nextTick()
      await this.waitForLayout()

      const el = this.$refs.textAccordion?.$el
      if (!el) return

      const content = el.firstElementChild
      const height = content?.offsetHeight || el.scrollHeight
      if (!height) return

      this.collapsedHeight = height
      this.hasMeasured = true
      await this.$nextTick()
      this.animateReady = true
    },

    async expand() {
      if (this.isExpanded || !this.needsMore) return

      const accordion = this.$refs.textAccordion
      const accordionEl = accordion?.$el
      const beforeHeight =
        (this.hasMeasured && this.collapsedHeight > 0
          ? this.collapsedHeight
          : accordionEl?.offsetHeight) || 0

      this.isExpanded = true
      await this.$nextTick()
      await this.waitForLayout()

      const afterHeight = accordionEl?.scrollHeight || beforeHeight
      // Ensure we animate to the post-expand content height (isOpen may have
      // measured one frame too early before the rest text was laid out).
      if (afterHeight > beforeHeight) {
        accordion?.animateToHeight?.(afterHeight)
      }

      this.$emit('expand', {
        heightDelta: Math.max(0, afterHeight - beforeHeight),
        cardEl: this.$refs.card,
      })
    },
  },
}
</script>

<style lang="scss" module>
.card {
  break-inside: avoid;
  page-break-inside: avoid;
  display: block;
  width: 100%;
  margin: 0 0 16px;
  background-color: $color-white;
  border-radius: 6px;
  box-shadow: 0 0 5px 0 $color-box-shadow;
  padding: 18px 18px 16px;
  box-sizing: border-box;
  text-align: left;
}

.cardTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.authorName {
  font-weight: 600;
  font-size: 15px;
  color: $color-brand-navy;
  min-width: 0;
}

.fbBadge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: $color-facebook;
  background-color: $color-facebook-bg;
  border-radius: 999px;
  padding: 4px 9px;
  white-space: nowrap;
}

.fbIcon {
  width: 12px;
  height: 12px;
}

.textAccordion {
  // Accordion root is a clipping wrapper only; keep paragraph spacing on reviewText.
  display: block;
}

.reviewText {
  font-size: 14.5px;
  color: $color-brand-navy;
  margin: 0;
  line-height: 1.55;
}

.readMore {
  display: inline;
  border: 0;
  background: none;
  padding: 0;
  margin: 0;
  font: inherit;
  font-weight: 600;
  color: $color-grey-3;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: $color-brand-navy;
  }
}

.cardFooter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid $color-brand-sand;
}

.translated {
  font-size: $font-size-smaller;
  font-style: italic;
  color: $color-grey-3;
}

.fbLink {
  font-size: $font-size-smaller;
  font-weight: 600;
  color: $color-facebook;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &:hover {
    text-decoration: underline;
  }
}
</style>
